import React, { FunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Formik, FormikHelpers, Field, FormikProps } from 'formik';
import { object, string } from 'yup';

// utils
import isEmpty from 'lodash/isEmpty';

// selectors
import { getAdminMerchantIntegrationsState } from 'ducks/adminDucks/integrations/selectors';

// actions
import { addAppleInAppIntegration } from 'ducks/adminDucks/integrations/actions';

// Components
import { Button, Grid } from '@inplayer-org/inplayer-ui';
import FieldError from 'components/Fields/FieldError';
import StyledContainer from 'components/StyledContainer';
import TextArea from 'components/Fields/TextArea';

const { Container, Cell } = Grid;

interface FormValues {
  secretKey: string;
}

interface OwnProps {
  handleCloseModal: () => any;
}

type Props = OwnProps;

const validationSchema = object().shape({
  secretKey: string().required('*Required'),
});

const AppleInAppIntegrationForm: FunctionComponent<Props> = ({ handleCloseModal }) => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const merchantId = Number(id);

  const { appleInAppKeys } = useSelector(getAdminMerchantIntegrationsState);

  const { secret_key: secretKey } = appleInAppKeys;

  const initialValues = {
    secretKey: secretKey || '',
  };

  const onFormSubmit = async (values: FormValues, { setSubmitting, resetForm }: FormikHelpers<FormValues>) => {
    const { secretKey: secret_key } = values;
    const metadata = { secret_key };

    try {
      await dispatch(addAppleInAppIntegration({ metadata, id: merchantId }));
      handleCloseModal();
      resetForm();
      setSubmitting(false);
    } catch (e) {
      resetForm();
      setSubmitting(false);
    }
  };

  const handleClose = (resetForm: () => void) => () => {
    resetForm();
    handleCloseModal();
  };

  const content = isEmpty(appleInAppKeys) ? 'Save' : 'Update';

  return (
    <Formik<FormValues>
      enableReinitialize
      onSubmit={onFormSubmit}
      validationSchema={validationSchema}
      initialValues={initialValues}
    >
      {({ handleSubmit, isSubmitting, resetForm, isValid, dirty }: FormikProps<FormValues>) => {
        const disableSubmitButton = !isValid || isSubmitting || !dirty;

        return (
          <form onSubmit={handleSubmit}>
            <Container alignContent="space-around" columns={1}>
              <Cell>
                <Field name="secretKey" component={TextArea} label="App-Specific Shared Secret" />
                <FieldError name="secretKey" />
              </Cell>
            </Container>
            <StyledContainer columns={3} alignContent="center" justifyContent="center" margin="2rem 0 0 0">
              <Cell middle left={2}>
                <Button type="button" fullWidth onClick={handleClose(resetForm)}>
                  Cancel
                </Button>
              </Cell>
              <Cell middle left={3}>
                <Button type="submit" fullWidth buttonModifiers={['buttonPrimary']} disabled={disableSubmitButton}>
                  {content}
                </Button>
              </Cell>
            </StyledContainer>
          </form>
        );
      }}
    </Formik>
  );
};

export default AppleInAppIntegrationForm;
